import { createTransformedImports, isBabelApi } from './utils.js';
const PROMISE_STATIC_METHODS = new Set(['all', 'race', 'any', 'allSettled', 'resolve', 'reject', 'withResolvers']);
function createSignaliumPromiseMethodsTransform(api, opts) {
    const transformedImports = createTransformedImports([
        ['callback', ['signalium']],
        ['reactive', ['signalium']],
        ['reactiveMethod', ['signalium']],
        ['relay', ['signalium']],
        ['task', ['signalium']],
        ['watcher', ['signalium']],
    ], opts?.transformedImports, opts?.importPaths);
    const t = api.types;
    const promiseImportPath = opts?.promiseImportPath ?? 'signalium';
    const isTrackedImport = (localName, path) => {
        const binding = path.scope.getBinding(localName);
        if (!binding || !t.isImportSpecifier(binding.path.node))
            return false;
        const importSpec = binding.path.node;
        const importedName = importSpec.imported.name;
        const importDecl = binding.path.parent;
        if (!t.isImportDeclaration(importDecl))
            return false;
        const importPaths = transformedImports.get(importedName);
        if (!importPaths)
            return false;
        return importPaths.some(p => typeof p === 'string' ? importDecl.source.value === p : p.test(importDecl.source.value));
    };
    const isReactiveCall = (path) => {
        if (!t.isCallExpression(path.node))
            return false;
        const callee = path.node.callee;
        if (!t.isIdentifier(callee))
            return false;
        return isTrackedImport(callee.name, path);
    };
    const isWithinTrackedCall = (path) => {
        let current = path.parentPath;
        while (current) {
            if (current.isCallExpression() && isReactiveCall(current))
                return true;
            current = current.parentPath;
        }
        return false;
    };
    function ensureReactivePromiseIdentifier(programPath) {
        for (const bodyPath of programPath.get('body')) {
            if (!bodyPath.isImportDeclaration())
                continue;
            const importDecl = bodyPath.node;
            if (importDecl.source.value !== promiseImportPath)
                continue;
            for (const spec of importDecl.specifiers) {
                if (spec.type === 'ImportSpecifier') {
                    const ispec = spec;
                    const imported = ispec.imported;
                    if (imported && imported.name === 'ReactivePromise') {
                        return ispec.local.name;
                    }
                }
            }
        }
        for (const bodyPath of programPath.get('body')) {
            if (!bodyPath.isImportDeclaration())
                continue;
            const node = bodyPath.node;
            if (node.source.value !== promiseImportPath)
                continue;
            const localName = 'ReactivePromise';
            node.specifiers.push(t.importSpecifier(t.identifier(localName), t.identifier('ReactivePromise')));
            return localName;
        }
        const localName = 'ReactivePromise';
        const importDecl = t.importDeclaration([t.importSpecifier(t.identifier(localName), t.identifier('ReactivePromise'))], t.stringLiteral(promiseImportPath));
        const [first] = programPath.get('body');
        if (first) {
            first.insertBefore(importDecl);
        }
        else {
            programPath.pushContainer('body', importDecl);
        }
        return localName;
    }
    return {
        name: 'signalium-transform-reactive-promise-methods',
        visitor: {
            CallExpression(callPath) {
                if (!isWithinTrackedCall(callPath))
                    return;
                const callee = callPath.node.callee;
                if (!t.isMemberExpression(callee))
                    return;
                if (callee.computed)
                    return;
                const object = callee.object;
                const property = callee.property;
                if (!t.isIdentifier(object, { name: 'Promise' }))
                    return;
                if (callPath.scope.getBinding('Promise'))
                    return;
                if (!t.isIdentifier(property))
                    return;
                const methodName = property.name;
                if (!PROMISE_STATIC_METHODS.has(methodName))
                    return;
                const programPath = callPath.findParent((p) => p.isProgram());
                const reactivePromiseId = ensureReactivePromiseIdentifier(programPath);
                const newCallee = t.memberExpression(t.identifier(reactivePromiseId), t.identifier(methodName));
                callPath.node.callee = newCallee;
            },
        },
    };
}
export function signaliumPromiseMethodsTransform(apiOrOpts, maybeOpts) {
    if (isBabelApi(apiOrOpts)) {
        return createSignaliumPromiseMethodsTransform(apiOrOpts, maybeOpts);
    }
    return (api) => createSignaliumPromiseMethodsTransform(api, apiOrOpts);
}
